import { heapAddEventProperties, heapTrackProperties } from '@m/utils/heap';

import { getSiteManagerUrl } from './parkNowHeap';
import {
  getReservationFunnelContext,
  syncReservationFunnelEventProperties,
} from './reservationFunnelContext';

/**
 * Heap tracking for Discovery Map — Funnel metrics (search → results → detail → checkout).
 */

type AuthOpts = {
  authenticated?: boolean;
  userAddedAt?: number | null;
};

const withFunnel = <T extends Record<string, string | number | boolean | undefined>>(
  props: T,
  authOpts?: AuthOpts,
) => ({
  ...getReservationFunnelContext(authOpts),
  ...props,
  timestamp: Date.now(),
});

export const trackDiscoveryLandingViewed = (authOpts?: AuthOpts) => {
  syncReservationFunnelEventProperties(authOpts);
  heapTrackProperties(
    'DiscoveryMap:Landing:Viewed',
    withFunnel(
      {
        'timestamp(utc)': new Date().toUTCString(),
      },
      authOpts,
    ),
  );
};

export const trackDiscoveryMapPageView = (authOpts?: AuthOpts) => {
  syncReservationFunnelEventProperties(authOpts);
  heapTrackProperties(
    'DiscoveryMap:Page:Viewed',
    withFunnel(
      {
        'timestamp(utc)': new Date().toUTCString(),
      },
      authOpts,
    ),
  );
};

export const trackDiscoveryMapPageRender = ({
  authenticated,
  geolocationStatus,
  hasClosestRegion,
  initialLatitude,
  initialLongitude,
  userAddedAt,
}: {
  authenticated?: boolean;
  geolocationStatus: 'success' | 'denied' | 'unavailable' | 'pending';
  hasClosestRegion: boolean;
  initialLatitude?: number;
  initialLongitude?: number;
  userAddedAt?: number | null;
}) => {
  heapTrackProperties(
    'DiscoveryMap:Page:Render',
    withFunnel(
      {
        geolocationStatus,
        hasClosestRegion,
        initialLatitude,
        initialLongitude,
      },
      { authenticated, userAddedAt },
    ),
  );
};

export const trackDiscoveryGaragePinClick = ({
  action,
  latitude,
  longitude,
  siteId,
  siteName,
}: {
  action: 'select' | 'deselect';
  latitude: number;
  longitude: number;
  siteId: number | string;
  siteName?: string;
}) => {
  heapTrackProperties(
    'DiscoveryMap:GaragePin:Click',
    withFunnel({
      action,
      latitude,
      longitude,
      siteId,
      siteManagerUrl: getSiteManagerUrl(siteId),
      siteName: siteName || '',
    }),
  );

  if (action === 'select') {
    heapAddEventProperties({
      lastSelectedSiteId: siteId,
      lastSelectedSiteName: siteName || '',
    });
  }
};

export const trackDiscoveryLocationSearch = ({
  resultsCount,
  searchQuery,
}: {
  resultsCount: number;
  searchQuery: string;
}) => {
  heapTrackProperties(
    'DiscoveryMap:Location:Search',
    withFunnel({
      resultsCount,
      searchQuery,
    }),
  );
};

export const trackDiscoveryLocationSelect = ({
  address,
  latitude,
  longitude,
}: {
  address: string;
  latitude: number;
  longitude: number;
}) => {
  heapTrackProperties(
    'DiscoveryMap:Location:Select',
    withFunnel({
      address,
      latitude,
      longitude,
    }),
  );
};

export const trackDiscoveryLocationCardAction = ({
  action,
  siteId,
  siteName,
}: {
  action: 'open' | 'close';
  siteId: number | string;
  siteName?: string;
}) => {
  heapTrackProperties(
    'DiscoveryMap:LocationCard:Action',
    withFunnel({
      action,
      siteId,
      siteManagerUrl: getSiteManagerUrl(siteId),
      siteName: siteName || '',
    }),
  );
};

/** Result → detail rate: fire when a search-result location detail is opened. */
export const trackDiscoveryLocationDetailViewed = ({
  resultsCount,
  siteId,
  siteName,
  source,
}: {
  resultsCount?: number;
  siteId: number | string;
  siteName?: string;
  source: 'map_pin' | 'list' | 'card' | 'book_now_no_dates';
}) => {
  heapTrackProperties(
    'DiscoveryMap:LocationDetail:Viewed',
    withFunnel({
      resultsCount,
      siteId,
      siteManagerUrl: getSiteManagerUrl(siteId),
      siteName: siteName || '',
      source,
    }),
  );
  heapAddEventProperties({
    lastSelectedSiteId: siteId,
    lastSelectedSiteName: siteName || '',
  });
};

/**
 * Inventory results after a discovery query. Always fire — including zero results —
 * so Heap can compute zero-result rate.
 */
export const trackDiscoverySitesLoaded = ({
  newSitesCount,
  searchRadius,
  sitesCount,
}: {
  newSitesCount: number;
  searchRadius: number;
  sitesCount: number;
}) => {
  const isZeroResult = sitesCount === 0;
  heapTrackProperties(
    'DiscoveryMap:Sites:Loaded',
    withFunnel({
      hasInventory: !isZeroResult,
      isZeroResult,
      newSitesCount,
      searchRadius,
      sitesCount,
    }),
  );
};

export const trackDiscoverySearchBarInput = ({
  action,
  searchQuery,
}: {
  action: 'typing' | 'clear' | 'focus';
  searchQuery: string;
}) => {
  heapTrackProperties(
    'DiscoveryMap:SearchBar:Input',
    withFunnel({
      action,
      searchQuery,
    }),
  );
};

export const trackDiscoveryDirectionsClick = ({
  address,
  platform,
  siteId,
  siteName,
}: {
  address: string;
  platform: 'apple_maps' | 'google_maps';
  siteId: number | string;
  siteName?: string;
}) => {
  heapTrackProperties(
    'DiscoveryMap:Directions:Click',
    withFunnel({
      address,
      platform,
      siteId,
      siteManagerUrl: getSiteManagerUrl(siteId),
      siteName: siteName || '',
    }),
  );
};

/** Funnel step: search (date/location window submit). */
export const trackDiscoveryFindParkingSubmit = ({
  end,
  location,
  start,
}: {
  end?: number;
  location?: string;
  start?: number;
}) => {
  heapTrackProperties(
    'DiscoveryMap:FindParking:Submit',
    withFunnel({
      end,
      location: location || '',
      start,
    }),
  );

  if (start != null || end != null) {
    heapAddEventProperties({
      reservationStart: start,
      reservationEnd: end,
    });
  }
};

/** Funnel step: search → checkout entry. */
export const trackDiscoveryBookNowClick = ({
  end,
  siteId,
  siteName,
  start,
  zone,
}: {
  end?: number;
  siteId: number | string;
  siteName?: string;
  start?: number;
  zone?: string | number;
}) => {
  heapTrackProperties(
    'DiscoveryMap:BookNow:Click',
    withFunnel({
      end,
      siteId,
      siteManagerUrl: getSiteManagerUrl(siteId),
      siteName: siteName || '',
      start,
      zone: zone ?? siteId,
    }),
  );

  heapAddEventProperties({
    lastSelectedSiteId: siteId,
    lastSelectedSiteName: siteName || '',
    reservationStart: start,
    reservationEnd: end,
  });
};
